import {useEffect,useState,type FormEvent} from 'react';
import {DEFAULT_HELM_HEADER} from '../assets/helmHeader';

export type AccountAccess={
  actorKind:'TEAM_MEMBER'|'CUSTOMER';
  staffMode:boolean;
  calendarLocations:string[];
  defaultLocationCode?:string|null;
  canManageTeam?:boolean;
  canApproveVacations?:boolean;
  canManageLocationSettings?:boolean;
  locationSettingsLocations?:string[];
};

/** Display the owner-provided Helm Header inside the gradient masthead,
 * above the public tabs, not inside the login form beneath them. */
export function LoginMastheadImage({locationCode}:{locationCode?:string}){
  const [branding,setBranding]=useState({showLoginMasthead:true,hasCustomImage:false});

  useEffect(()=>{
    if(!locationCode)return;
    const controller=new AbortController();
    void fetch('/api/v1/shop/branding?'+new URLSearchParams({location:locationCode}),{
      signal:controller.signal,cache:'no-store',
    }).then(async response=>{
      if(!response.ok)return;
      const json=await response.json() as {ok:boolean;data?:{
        showLoginMasthead:boolean;hasCustomImage:boolean}};
      if(json.ok&&json.data&&!controller.signal.aborted)setBranding(json.data);
    }).catch(()=>{});
    return ()=>controller.abort();
  },[locationCode]);

  const bannerSrc=branding.hasCustomImage&&locationCode?
    '/api/v1/shop/masthead?'+new URLSearchParams({location:locationCode}):
    DEFAULT_HELM_HEADER;
  return branding.showLoginMasthead?
    <img className="ecfs-login-header-image" src={bannerSrc}
      alt="" aria-hidden="true"/>:null;
}

export function LoginScreen({onAuthenticated,locationCode}:{
  onAuthenticated:(access:AccountAccess)=>void;locationCode?:string;
}) {
  const [email,setEmail]=useState('');
  const [firstName,setFirstName]=useState('');
  const [lastName,setLastName]=useState('');
  const [phone,setPhone]=useState('');
  const [referral,setReferral]=useState('');
  const [referralOptions,setReferralOptions]=useState<string[]>([]);
  const [password,setPassword]=useState('');
  const [busy,setBusy]=useState(false);
  const [creating,setCreating]=useState(false);
  const [remember,setRemember]=useState(false);
  const [message,setMessage]=useState('');

  useEffect(()=>{
    if(!locationCode){setReferralOptions([]);return;}
    const controller=new AbortController();
    setReferralOptions([]);setReferral('');
    void fetch('/api/v1/public/signup-options?'+new URLSearchParams({
      location:locationCode}),{credentials:'same-origin',cache:'no-store',
      signal:controller.signal})
      .then(async response=>{
        if(!response.ok)return;
        const value=await response.json() as {ok:boolean;data?:{referralOptions:string[]}};
        if(!controller.signal.aborted&&value.ok&&
          Array.isArray(value.data?.referralOptions))
          setReferralOptions(value.data.referralOptions);
      }).catch(()=>{});
    return ()=>controller.abort();
  },[locationCode]);
  const login=async(event:FormEvent<HTMLFormElement>)=>{
    event.preventDefault();
    if(busy||!email.trim()||!password)return;
    setBusy(true);setMessage('');
    try{
      const response=await fetch(creating?'/api/v1/auth/register':'/api/v1/auth/login',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({email:email.trim(),password,
          ...(creating?{
            ...(locationCode?{location:locationCode}:{}),
            firstName,lastName,phone,referral,
          }:{remember})}),
      });
      if(response.status===404){
        setMessage(creating?'Account registration is not enabled in this staging environment yet.':
          'Sign-in is not enabled in this staging environment yet.');
        return;
      }
      if(!response.ok){
        setMessage(response.status===409?'This email already has an account. Please log in.':
          response.status===401?'Email or password is incorrect.':
          creating?'Account registration is temporarily unavailable.':
          'Sign-in is temporarily unavailable.');return;
      }
      const details=await fetch('/api/v1/auth/access',{
        credentials:'same-origin',cache:'no-store',
      });
      if(!details.ok)throw new Error('Unable to load your account permissions.');
      const result=await details.json() as {ok:boolean;data?:AccountAccess};
      if(!result.ok||!result.data)throw new Error('Unable to load your account permissions.');
      if(result.data.actorKind==='TEAM_MEMBER'&&
        (!result.data.staffMode||result.data.calendarLocations.length===0)){
        setMessage('Your account is signed in, but does not have staff calendar access.');
        return;
      }
      onAuthenticated(result.data);
    }catch(error){
      setMessage(error instanceof Error?error.message:'Sign-in is temporarily unavailable.');
    }finally{setBusy(false);}
  };

  const resetPassword=async()=>{
    if(!email.trim()){setMessage('Enter your email address first.');return;}
    setBusy(true);setMessage('');
    try{
      const response=await fetch('/api/v1/auth/reset-password',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({email:email.trim()}),
      });
      if(!response.ok){setMessage('Unable to send password reset email. Please try again.');return;}
      setMessage('If this email has a Firebase account, a password reset link has been sent.');
    }catch{setMessage('Unable to send password reset email. Please try again.');}
    finally{setBusy(false);}
  };

  return <section className="ecfs-login-screen" aria-label={creating?'Create an account':'Sign in'}>
    <form className="ecfs-login-form" onSubmit={event=>void login(event)}>
      {creating&&<h2>Create an Account</h2>}
      {creating&&<>
        <label>First Name
          <input aria-label="First Name" value={firstName}
            autoComplete="given-name" maxLength={100}
            onChange={event=>setFirstName(event.target.value)}/>
        </label>
        <label>Last Name
          <input aria-label="Last Name" value={lastName}
            autoComplete="family-name" maxLength={100}
            onChange={event=>setLastName(event.target.value)}/>
        </label>
      </>}
      <label>Email
        <input type="email" aria-label="Email" autoComplete="username"
          required value={email} maxLength={320}
          onChange={event=>setEmail(event.target.value)}/>
      </label>
      {creating&&<label>Phone Number
        <input type="tel" aria-label="Phone Number" autoComplete="tel"
          value={phone} maxLength={40}
          onChange={event=>setPhone(event.target.value)}/>
      </label>}
      <label>Password
        <input type="password" aria-label="Password"
          autoComplete={creating?'new-password':'current-password'}
          required value={password} minLength={creating?6:undefined} maxLength={256}
          onChange={event=>setPassword(event.target.value)}/>
      </label>
      {creating&&<>
        {referralOptions.length>0&&<label>How did you hear about us?
          <select aria-label="How did you hear about us?" value={referral}
            onChange={event=>setReferral(event.target.value)}>
            <option value="">Please Select</option>
            {referralOptions.map(option=><option key={option} value={option}>
              {option}</option>)}
          </select>
        </label>}
      </>}
      {!creating&&<label className="ecfs-remember-option">
        <input type="checkbox" checked={remember} disabled={busy}
          onChange={event=>setRemember(event.target.checked)}/> Keep me logged in
      </label>}
      <div className="ecfs-login-actions">
        <button type="submit" disabled={busy}>
          {busy?(creating?'Creating account…':'Signing in…'):(creating?'Create account':'Log In')}
        </button>
        <button type="button" disabled={busy}
          onClick={()=>{setCreating(value=>!value);setMessage('');setPassword('');}}>
          {creating?'Back to Log In':'Create an account'}
        </button>
      </div>
      {!creating&&<button type="button" className="ecfs-login-forgot"
        disabled={busy} onClick={()=>void resetPassword()}>
        Forgot your password?
      </button>}
      {message&&<p role="alert" className="ecfs-login-message">{message}</p>}
    </form>
  </section>;
}
